import type { Category, Item, Meal } from '@shared/types'

export interface MealGroup {
  category: Category
  items: Item[]
}

/**
 * A meal's components in category order.
 *
 * Meals store only item ids, so everything else is read from the pantry as it
 * is now -- rename an item or re-file it and every saved meal follows. An id
 * that no longer resolves is dropped: the item was deleted, and the meal is
 * simply a component short until someone edits it.
 */
export function groupComponents(
  meal: Meal,
  itemsById: Map<string, Item>,
  categories: Category[],
): MealGroup[] {
  const byCategory = new Map<string, Item[]>()
  for (const id of meal.itemIds) {
    const item = itemsById.get(id)
    if (!item) continue
    const list = byCategory.get(item.categoryId)
    if (list) list.push(item)
    else byCategory.set(item.categoryId, [item])
  }
  return categories
    .map((category) => ({ category, items: byCategory.get(category.id) ?? [] }))
    .filter((g) => g.items.length > 0)
}

/** The components as one line, for a card subtitle: "Bagel · Grapes · Carrots". */
export function componentLine(meal: Meal, itemsById: Map<string, Item>): string {
  return meal.itemIds
    .map((id) => itemsById.get(id)?.name)
    .filter((name): name is string => Boolean(name))
    .join(' · ')
}

/** Free-text search across a meal's name, notes, and what is in it. */
export function mealMatches(meal: Meal, itemsById: Map<string, Item>, needle: string): boolean {
  if (!needle) return true
  const haystack = [meal.name, meal.notes, componentLine(meal, itemsById)].join(' ').toLowerCase()
  return haystack.includes(needle)
}
